import styled from "@emotion/styled"
import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import { PText } from "@porsche-design-system/components-react"
import { spacingStaticSm, spacingStaticMd } from "@porsche-design-system/components-react/emotion"
import type { PropsWithChildren } from "react"
import { useIntl } from "react-intl"
import { getCustomerAppLanguageTags } from "src/common/getCustomerAppLanguageTags"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { getDirection } from "src/context/translations/useDirection"
import { alphaDisabled } from "../alphaDisabled"
import type { TextAreaFieldProps } from "../textArea/TextAreaField"
import { TextAreaField } from "../textArea/TextAreaField"

export interface LocalizedTextAreaFieldProps extends PropsWithChildren<TextAreaFieldProps> {
	/**
	 * Determines from which app the locales are derived from.
	 * @default dealer-app
	 */
	locales?: "dealer-app" | "customer-app"
}

export const LocalizedTextAreaField = (props: LocalizedTextAreaFieldProps) => {
	const { children, descriptionText, disabledHint, label, locales, name, required, ...rest } = props
	const { formatDisplayName } = useIntl()
	const { availableLocales, marketplaceKey } = useDealerContext()
	const customerAppLocales = getCustomerAppLanguageTags(marketplaceKey)

	const loc = locales === "customer-app" ? customerAppLocales : availableLocales

	const labelElement = (
		<PText color="primary">
			{label}
			{required && <>&nbsp;*</>}
		</PText>
	)

	return (
		<Layout>
			{labelElement && (
				<LabelGroup $disabled={!!disabledHint}>
					{labelElement}
					{descriptionText && (
						<PText color="contrast-medium" size="xs">
							{descriptionText}
						</PText>
					)}
				</LabelGroup>
			)}

			{/* used to inject other markup like ai button */}
			{children}

			<Wrapper>
				{loc.map((locale: string) => {
					const displayName = loc.length > 1 ? formatDisplayName(locale, { type: "language" }) : undefined
					return (
						<TextAreaField
							key={locale}
							dir={getDirection(locale)}
							{...rest}
							label={displayName}
							required={required}
							disabledHint={disabledHint}
							name={`${name}.${locale}`}
						/>
					)
				})}
			</Wrapper>
		</Layout>
	)
}

const Layout = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticSm};
`

const LabelGroup = styled("div", { shouldForwardProp })<{ $disabled: boolean }>`
	opacity: ${({ $disabled }) => ($disabled ? alphaDisabled : 1)};
`

const Wrapper = styled(Layout)`
	gap: ${spacingStaticMd};
`
